import { AgChartInstance } from "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import { AgChartOptions } from "ag-grid-enterprise";
import { useTheme } from "next-themes";
import { SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { darkAnalyticFills, lightAnalyticFills } from "../AnalyticsStyles";
import {
  filterByCountryName,
  filterByDates,
} from "./ClientSummaryReportHelpers";
import { IClientSummaryFilters } from "./IndexClientSummary";
import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";

type Item = {
  instrumentType: string;
  [key: string]: any; // other properties can exist but are ignored
};

function convertDataForPie(
  data: Item[],
  dataFilters: any,
): { instrumentType: string; amount: number }[] {
  const counts: Record<string, number> = {};
  const countryFiltered = filterByCountryName(data, dataFilters);
  const dateFilteredObject = filterByDates(countryFiltered, dataFilters);

  for (const item of dateFilteredObject) {
    counts[item.instrumentType] = (counts[item.instrumentType] || 0) + 1;
  }
  const filteredData = Object.entries(counts).map(
    ([instrumentType, amount]) => ({
      instrumentType,
      amount,
    }),
  );
  // Filter the return object.
  return filteredData;
}

const SecurityTypesPieChart = ({
  data,
  dataFilters,
  setDataFilters,
}: {
  data: IBaseAnalyticsCombined[];
  dataFilters: IClientSummaryFilters;
  setDataFilters: React.Dispatch<SetStateAction<IClientSummaryFilters>>;
}) => {
  const chartRef = useRef<AgChartInstance>(null);
  const { theme } = useTheme();

  const [pieData, setPieData] = useState(convertDataForPie(data, dataFilters));

  useEffect(() => {
    if (dataFilters && dataFilters.selectedInstruments.length == 0) {
      const newState = chartRef.current!.getState();
      const currentFilters = { ...dataFilters };
      if (newState && newState.legend && newState.legend.length > 0) {
        const newMappedTypes = newState.legend.map(
          (item) => item.legendItemName,
        );
        if (
          newMappedTypes &&
          newMappedTypes != undefined &&
          newMappedTypes.length > 0
        ) {
          currentFilters.selectedInstruments = newMappedTypes as string[];
        }
      }
      setDataFilters({ ...currentFilters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieData]);

  useEffect(() => {
    const updatedData = convertDataForPie(data, dataFilters);
    setPieData(updatedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    dataFilters.range,
    dataFilters.countryActive,
    dataFilters.activeDay,
  ]);

  const [legendUpdated, setLegendUpdated] = useState(true);

  const options = useMemo<AgChartOptions | any>(() => {
    return {
      theme: theme == "dark" ? "ag-default-dark" : "ag-default",
      data: pieData,
      width: 220,
      height: 140,
      background: {
        visible: false,
      },
      series: [
        {
          type: "pie",
          angleKey: "amount",
          legendItemKey: "instrumentType",
          outerRadiusOffset: -10,
          fills: theme == "dark" ? darkAnalyticFills : lightAnalyticFills,
        },
      ],
      legend: {
        position: "left",
        spacing: 2,
        item: {
          label: {
            formatter: (params: any) => params.value,
          },
        },
        listeners: {
          legendItemClick: () => {
            setLegendUpdated((currentState) => !currentState);
          },
        },
      },
      padding: {
        left: 1,
        right: 1,
        top: 1,
        bottom: 1,
      },
    };
  }, [pieData, theme]);

  useEffect(() => {
    const newState = chartRef.current!.getState();
    const currentFilters = { ...dataFilters };
    if (newState && newState.legend && newState.legend.length > 0) {
      const newMappedTypes = newState.legend
        .filter((item) => item.visible)
        .map((item) => item.legendItemName);
      if (
        newMappedTypes &&
        newMappedTypes != undefined &&
        newMappedTypes.length > 0
      ) {
        currentFilters.selectedInstruments = newMappedTypes as string[];
      } else if (newState.legend.filter((item) => item.visible).length == 0) {
        currentFilters.selectedInstruments = [];
      }
    }

    setDataFilters({ ...currentFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legendUpdated]);

  return (
    <div>
      <div className="relative top-3 ml-4">Instrument Types:</div>
      <div
        id="SecurityTypesPieChart"
        className="flex w-full overflow-hidden pl-4 pt-4"
      >
        <AgCharts ref={chartRef} options={options} />
      </div>
    </div>
  );
};

export default SecurityTypesPieChart;
